// ============================================================
// DeadlineReminderPrefs.tsx — Round 2: Settings › Notifications
// "Extra deadline reminders". Per deadline type, the FIRM's warnings show
// as locked chips (set in Admin › Deadline types — they can't be removed
// here) and the person can ADD their own days before. Stored in their
// notificationPreferences.deadlineExtraDays ({ [typeId]: days[] }) via the
// same PATCH /api/auth/me every notification switch uses; the backend
// sanitises it and the hourly scan unions it with the firm's schedule.
// ============================================================
import { refusalText } from '../../utils/serverError'
import React, { useEffect, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { listDeadlineTypesApi, updateMeApi } from '../../api/casevoApi'
import type { DeadlineType } from '../../types/deadlines.types'
import { DEFAULT_NOTIFICATION_PREFERENCES } from '../../types/notifications.types'
import WarningDayChips from '../deadlines/WarningDayChips'
import { SettingsCard, S_NOTE } from './settingsUi'

const DeadlineReminderPrefs: React.FC = () => {
  const { authUser, setAuthUser } = useCasevoStore()
  const [types, setTypes] = useState<DeadlineType[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listDeadlineTypesApi()
      .then((t) => { if (!cancelled) setTypes([...t].sort((a, b) => Number(b.isLimitation) - Number(a.isLimitation) || a.name.localeCompare(b.name))) })
      .catch(() => { if (!cancelled) setLoadError('Couldn’t load the deadline types.') })
    return () => { cancelled = true }
  }, [])

  if (!authUser) return null
  const prefs = { ...DEFAULT_NOTIFICATION_PREFERENCES, ...(authUser.notificationPreferences ?? {}) }
  const extras = prefs.deadlineExtraDays ?? {}

  // Optimistic, like the switches: store first, then save; roll back on failure.
  const save = async (typeId: string, days: number[]): Promise<void> => {
    const nextExtras = { ...extras }
    if (days.length) nextExtras[typeId] = days
    else delete nextExtras[typeId]
    const next = { ...prefs, deadlineExtraDays: nextExtras }
    setSavingId(typeId)
    setSaveError(null)
    setAuthUser({ ...authUser, notificationPreferences: next })
    try {
      setAuthUser(await updateMeApi({ notificationPreferences: next }))
    } catch (err) {
      setAuthUser({ ...authUser, notificationPreferences: prefs })
      setSaveError(refusalText(err, 'Couldn’t save your reminders — please try again.'))
    } finally {
      setSavingId(null)
    }
  }

  return (
    <SettingsCard icon="bell" title="Extra deadline reminders"
      sub="Add your own reminders before a deadline, on top of your firm’s. You can’t remove the firm’s warnings here. Changes save instantly.">
      {!prefs.emailBeforeDeadline && (
        <p className={`${S_NOTE} pb-[6px]`}>
          Deadline emails are off for you, so these reminders arrive in the bell only, except limitation dates, which always email.
        </p>
      )}
      {loadError ? (
        <p className="text-[13px] text-casevo-danger py-[13px]">{loadError}</p>
      ) : types === null ? (
        <p className={`${S_NOTE} py-[13px]`}>Loading deadline types…</p>
      ) : types.length === 0 ? (
        <p className={`${S_NOTE} py-[13px]`}>Your firm has no deadline types yet.</p>
      ) : (
        <ul>
          {types.map((t) => {
            const mine = (extras[t.id] ?? []).filter((d) => !t.warningDays.includes(d))
            return (
              <li key={t.id} className="py-[13px] border-t border-casevo-border-soft first:border-t-0">
                <div className="flex items-center gap-2 mb-[6px]">
                  <span className="text-[14px] text-casevo-forest">{t.name}</span>
                  {t.isLimitation && <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] px-1.5 py-px rounded bg-[#fbf1dc] text-[#7a5d10] dark:bg-[rgba(224,182,74,0.14)] dark:text-[#e0b64a]">Limitation</span>}
                  {savingId === t.id && <span className="text-[11.5px] text-casevo-text-soft">Saving…</span>}
                </div>
                <WarningDayChips
                  editable={mine}
                  locked={t.warningDays}
                  lockedTone="firm"
                  lockedTitle="Set by your firm (Admin › Deadline types) — can’t be removed here"
                  onChange={(next) => { void save(t.id, next) }}
                  disabled={savingId === t.id}
                  ariaLabel={`Your reminders for ${t.name}`}
                />
              </li>
            )
          })}
        </ul>
      )}
      {saveError && <p role="alert" className="text-[12.5px] text-casevo-danger pt-[6px]">{saveError}</p>}
      <p className={`${S_NOTE} pt-[8px]`}>Everyone is also alerted on the due day and on the first overdue day, then gets one daily “Overdue deadlines” email until each is completed.</p>
    </SettingsCard>
  )
}

export default DeadlineReminderPrefs
